import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, Event as RouterEvent, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { ProfileSearchService, ProfileSearchParams } from '../../../../../profiles/search/index';
import { UserService } from '../../../../../users/shared/user.service';
import { NavigationService } from '../../../../../shared/navigation/navigation.service';
import { AsyncState } from '../../../../../shared/model/index';
import { WlProfilesToolbarBroadcastService, WlProfilesToolbarEventTypeEnum, WlProfilesToolbarEvent } from './broadcast/index';
import { UtilService } from 'src/app/shared/util/util.service';

/**
 * Toolbar that goes at the top of the app.
 * This toolbar is for profiles.
 */
@Component({
  selector: 'wl-module-profiles-toolbar',
  templateUrl: 'toolbar-module-profiles.component.html',
  styleUrls: ['toolbar-module-profiles.component.less'],
  providers: [ProfileSearchService],
    standalone: false
})
export class WlModuleProfilesToolbarComponent implements OnInit, OnDestroy {
  public static _storedKeyword = '';

  public get keyword(): string {
    return WlModuleProfilesToolbarComponent._storedKeyword;
  }
  public set keyword(value: string) {
    WlModuleProfilesToolbarComponent._storedKeyword = value;
  }

  public _userProfilesAccessLevel = 0;
  public _modelChangeDebounceTimeoutId = 0;
  public _inlineSearching: AsyncState = new AsyncState();
  public _searching: AsyncState = new AsyncState();
  public _inlineSearchResults: Array<any> = [];
  public _resultsOpen = false;
  public _isProfileSearchPage = false;
  public _subscriptions: Array<Subscription> = [];
  public _cancelLastInlineSearch = false;

  /**
   * injection
   * @param _router
   * @param _route
   * @param _profilesService
   * @param _userService
   * @param _navigationService
   * @param _profilesToolbarBroadcastService
   */
  public constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _profilesService: ProfileSearchService,
    private _userService: UserService,
    private _navigationService: NavigationService,
    private _profilesToolbarBroadcastService: WlProfilesToolbarBroadcastService,
    private _utilService: UtilService
  ) {}

  /**
   * setup
   */
  public ngOnInit(): void {
    // get this user's permissions
    this._userService.getCurrentUserSecurityElementAccessLevel('Profiles_Module').then((userAccessLevel: number) => {
      this._userProfilesAccessLevel = userAccessLevel;
    });

    // initial load: to see if we are on the event advanced search page
    this._isProfileSearchPage = this.isProfileSearchUrl(this._router.url);

    // watch to see if we are on the profile advanced search page
    this._subscriptions.push(
      this._router.events.subscribe((event: RouterEvent) => {
        if (!(event instanceof NavigationEnd)) {
          return;
        }

        const navigationEndEvent: NavigationEnd = event as any as NavigationEnd;
        const url: string = navigationEndEvent.urlAfterRedirects == null ? '' : navigationEndEvent.urlAfterRedirects.toLocaleLowerCase();

        this._isProfileSearchPage = this.isProfileSearchUrl(url);
      })
    );

    // watch the query param for events
    this._subscriptions.push(
      this._route.queryParams.subscribe((params: any) => {
        if (this._isProfileSearchPage && params && params.q) {
          this.keyword = params.q;
        }
      })
    );

    // watch for events sent to the toolbar
    this._subscriptions.push(
      this._profilesToolbarBroadcastService.subscribe((event: WlProfilesToolbarEvent) => {
        // started searching/stopped searching
        if (event.type === WlProfilesToolbarEventTypeEnum.searching) {
          this._searching.busy = event.data as boolean;
        }

        // some component (that is not the parent) needs the keyword
        if (event.type === WlProfilesToolbarEventTypeEnum.requestKeyword) {
          this._profilesToolbarBroadcastService.broadcast({
            type: WlProfilesToolbarEventTypeEnum.requestKeywordAnswer,
            data: this.keyword,
          });
        }
      })
    );
  }

  /**
   * clean up
   */
  public ngOnDestroy(): void {
    // clean up subscriptions
    if (this._subscriptions) {
      this._subscriptions.forEach((sub) => sub.unsubscribe());
    }
  }

  /**
   * do an inline search
   */
  public inlineSearch(): void {
    this._inlineSearching.busy = true;
    this._inlineSearching.success = false;
    this._inlineSearching.message = '';

    // keep track of requests in case they come back really fast
    const currentRequestId: number = ++this._inlineSearching.requestId;

    // only get next most recent
    const searchParams: ProfileSearchParams = new ProfileSearchParams();
    searchParams.OrderBy = 'Member: DESC, ASF: ASC';
    searchParams.PageSize = 10;
    searchParams.PageNumber = 1;

    searchParams.Keyword = this.keyword;

    this._cancelLastInlineSearch = false;

    this._profilesService.search(searchParams).then(
      (result: any) => {
        if (currentRequestId != this._inlineSearching.requestId) {
          return;
        }

        this._inlineSearching.busy = false;
        this._inlineSearching.success = true;
        this._inlineSearching.message = '';

        this._inlineSearchResults = result && result.Result ? result.Result : [];

        if (!this._cancelLastInlineSearch) {
          this.openResults();
        }

        // reset
        this._cancelLastInlineSearch = false;
      },
      (error: any) => {
        if (currentRequestId != this._inlineSearching.requestId) {
          return;
        }

        this._inlineSearching.busy = false;
        this._inlineSearching.success = false;
        this._inlineSearching.message = this._utilService.extractErrorMessage(error);

        if (!this._cancelLastInlineSearch) {
          this.openResults();
        }

        // reset
        this._cancelLastInlineSearch = false;
      }
    );
  }

  /**
   * open the result panel
   */
  public openResults(): void {
    this._resultsOpen = true;
  }

  /**
   * close the result panel
   */
  public closeResults(): void {
    this._resultsOpen = false;
  }

  /**
   * helper method that determines if the given url is the events dashboard url
   * @param testUrl
   */
  public isProfileSearchUrl(testUrl: string) {
    testUrl = testUrl.toLowerCase();
    return testUrl.startsWith('/profiles/') || testUrl.startsWith('/profiles?') || testUrl == '/profiles';
  }

  /**
   * triggered by submitting the search form
   */
  public handleSearchFormSubmit(): void {
    // go to advanced search
    this._navigationService.navigateToProfileFinder({ q: this.keyword });

    clearTimeout(this._modelChangeDebounceTimeoutId);
    this._cancelLastInlineSearch = true;

    this.closeResults();
  }

  /**
   * triggered by clicking the reset button
   */
  public handleResetClick(): void {
    this.keyword = '';

    if (this._isProfileSearchPage) {
      this._navigationService.navigateToProfileFinder({ q: this.keyword });
    }

    this._profilesToolbarBroadcastService.broadcast(new WlProfilesToolbarEvent(WlProfilesToolbarEventTypeEnum.clearKeyword));
  }

  /**
   * triggerd on model change
   */
  public handleModelChange(): void {
    clearTimeout(this._modelChangeDebounceTimeoutId);

    if (this.keyword && (this.keyword.trim().length > 2 || /^\d+$/.test(this.keyword))) {
      this._modelChangeDebounceTimeoutId = window.setTimeout(() => this.inlineSearch(), 550);
    }
  }

  /**
   * triggerd by new event button click
   */
  public handleNewProfileClick(): void {
    // has access?
    if (this._userProfilesAccessLevel >= 2) {
      this._profilesToolbarBroadcastService.broadcast(new WlProfilesToolbarEvent(WlProfilesToolbarEventTypeEnum.newProfile));
    }
  }

  /**
   * handle document clicks
   */
  @HostListener('document:click')
  public handleDocumentClick() {
    this.closeResults();
  }

  public navigateToProfileEdit(profileId: number) {
    this._navigationService.navigateToProfileEdit(profileId);
  }

  public navigateToViewTransactions(profileId: number) {
    this._navigationService.navigateToTransactionsView(profileId);
  }
}
